import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Phone, ArrowRight, CheckCircle, Calculator, Clock, Truck, Info } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { MobileCTA } from '../components/MobileCTA';
import { businessConfig, trackPhoneClick } from '../config/business';

export default function PricingPage() {
  const { pricing, contact, howItWorks } = businessConfig;

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Removalist Pricing Melbourne | From $130/hr | Thrifty Move</title>
        <meta name="description" content="Professional Melbourne removalist pricing from $130/hour including 2 removalists and GST. 4-6 Tonne $130/hr, 8-10 Tonne $140/hr, 12+ Tonne from $187/hr. 2-hour minimum. No hidden fees." />
        <link rel="canonical" href="https://thrifty-move.com.au/pricing" />
        <meta property="og:title" content="Removalist Pricing Melbourne | From $130/hr | Thrifty Move" />
        <meta property="og:description" content="Transparent Melbourne removalist pricing from $130/hour. Volume-based tiers: 4-6 Tonne $130/hr, 8-10 Tonne $140/hr, 12+ Tonne from $187/hr. GST included." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://thrifty-move.com.au/pricing" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "PriceSpecification",
            "name": "Melbourne Removalist Services",
            "priceCurrency": "AUD",
            "price": "130",
            "minPrice": "130",
            "maxPrice": "187",
            "unitText": "per hour",
            "description": "Professional removalist services from $130/hr including 2 removalists and GST. 2-hour minimum booking.",
            "eligibleQuantity": {
              "@type": "QuantitativeValue",
              "minValue": 2,
              "unitText": "hours"
            }
          })}
        </script>
      </Helmet>

      <Header />

      {/* Hero */}
      <section className="bg-gray-900 pt-28 md:pt-32 pb-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Transparent Pricing
            </h1>
            <p className="text-xl text-gray-300 mb-6">
              Volume-based pricing tiers. Your rate depends on your estimated move size.
            </p>
            <p className="text-gray-400">
              No hidden fees. The price we quote is the price you pay.
            </p>
          </div>
        </div>
      </section>

      {/* Pricing Tiers */}
      <section className="py-16 md:py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            
            {/* Tier Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
              {pricing.tiers.map((tier, i) => (
                <Card 
                  key={i} 
                  className={`border-2 ${i === 1 ? 'border-yellow-500 shadow-xl scale-105' : 'border-gray-200'} relative overflow-hidden`}
                >
                  {i === 1 && (
                    <div className="absolute top-0 left-0 right-0 bg-yellow-500 text-gray-900 text-center text-sm font-bold py-1">
                      Most Common
                    </div>
                  )}
                  <CardContent className={`p-6 text-center ${i === 1 ? 'pt-10' : 'pt-6'}`}>
                    <div className="text-lg font-bold text-gray-900 mb-1">{tier.label}</div>
                    <div className="text-sm font-medium text-gray-500 mb-3">{tier.volumeRange}</div>
                    <div className="text-5xl font-bold text-gray-900 mb-1">
                      {tier.hourlyRateDisplay || `$${tier.hourlyRate}`}
                    </div>
                    <div className="text-gray-500 mb-4">per hour</div>
                    <p className="text-gray-600 text-sm mb-6">{tier.description}</p>
                    <Link to="/quote">
                      <Button 
                        className={`w-full ${i === 1 ? 'bg-yellow-500 hover:bg-yellow-400 text-gray-900' : 'bg-gray-900 hover:bg-gray-800 text-white'} font-semibold`}
                      >
                        Get a Quote
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* What's Included */}
            <Card className="border-2 border-gray-200 mb-8">
              <CardContent className="p-6 md:p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">What's Included</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {pricing.includes.map((item, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <CheckCircle className="w-5 h-5 text-green-600" />
                      </div>
                      <span className="font-medium text-gray-900">{item}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Pricing Modifiers */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12 max-w-2xl mx-auto">
              <Card className="border border-gray-200 bg-gray-50">
                <CardContent className="p-6">
                  <h3 className="font-bold text-gray-800 mb-2">Mon–Sat Rates</h3>
                  <p className="text-gray-700">Standard rates apply</p>
                  <p className="text-sm text-gray-600 mt-2">No surcharges Monday to Saturday.</p>
                </CardContent>
              </Card>
              <Card className="border border-amber-200 bg-amber-50">
                <CardContent className="p-6">
                  <h3 className="font-bold text-amber-800 mb-2">Sunday Rate</h3>
                  <p className="text-amber-700">{pricing.modifiers.sundaySurcharge}</p>
                  <p className="text-sm text-amber-600 mt-2">Additional for Sunday moves.</p>
                </CardContent>
              </Card>
            </div>

            {/* Call-out Fee Note */}
            <Card className="border border-gray-200 bg-gray-50 mb-12">
              <CardContent className="p-6 flex items-start gap-4">
                <Info className="w-6 h-6 text-gray-500 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-bold text-gray-900 mb-1">Travel / Call-out Fee</h3>
                  <p className="text-gray-600">{pricing.calloutNote}</p>
                </div>
              </CardContent>
            </Card>

            {/* How Volume Works */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">How Volume Determines Your Rate</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <Card className="border border-gray-200">
                  <CardContent className="p-6 text-center">
                    <Calculator className="w-10 h-10 text-yellow-500 mx-auto mb-4" />
                    <h3 className="font-bold text-gray-900 mb-2">Build Your Inventory</h3>
                    <p className="text-gray-600 text-sm">Use our online quote system to list all the items you're moving.</p>
                  </CardContent>
                </Card>
                <Card className="border border-gray-200">
                  <CardContent className="p-6 text-center">
                    <Truck className="w-10 h-10 text-yellow-500 mx-auto mb-4" />
                    <h3 className="font-bold text-gray-900 mb-2">We Calculate Volume</h3>
                    <p className="text-gray-600 text-sm">The system estimates your total move volume in cubic metres.</p>
                  </CardContent>
                </Card>
                <Card className="border border-gray-200">
                  <CardContent className="p-6 text-center">
                    <CheckCircle className="w-10 h-10 text-yellow-500 mx-auto mb-4" />
                    <h3 className="font-bold text-gray-900 mb-2">Get Your Rate</h3>
                    <p className="text-gray-600 text-sm">Your volume determines the appropriate pricing tier and truck capacity.</p>
                  </CardContent>
                </Card>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">How It Works</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {howItWorks.map((step, i) => (
                <div key={i} className="text-center">
                  <div className="w-14 h-14 bg-yellow-500 rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold text-gray-900">
                    {step.step}
                  </div>
                  <h3 className="font-bold text-gray-900 mb-2">{step.title}</h3>
                  <p className="text-gray-600 text-sm">{step.description}</p>
                </div>
              ))}
            </div>
            <p className="text-center text-gray-500 text-sm mt-8">
              Submitting your quote is a request, not an automatic booking. Our team confirms availability and final details.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-yellow-500">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            Get Your Personalised Quote
          </h2>
          <p className="text-gray-800 mb-8 max-w-xl mx-auto">
            Build your inventory online and see exactly what your move will cost.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/quote">
              <Button className="w-full sm:w-auto bg-gray-900 hover:bg-gray-800 text-white font-bold px-10 py-5 text-lg">
                <ArrowRight className="mr-2 w-5 h-5" />
                Start Your Quote
              </Button>
            </Link>
            <a href={contact.phoneLink} onClick={() => trackPhoneClick('Pricing - Call')}>
              <Button variant="outline" className="w-full sm:w-auto border-gray-900 text-gray-900 hover:bg-gray-900 hover:text-white font-bold px-10 py-5 text-lg">
                <Phone className="mr-2 w-5 h-5" />
                {contact.phone}
              </Button>
            </a>
          </div>
        </div>
      </section>

      <Footer />
      <MobileCTA />
    </div>
  );
}
